import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import { AlertTriangle, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { LoadingPage } from "../components/LoadingSpinner";
import { useAuth } from "../contexts/AuthContext";
import { useTenant } from "../contexts/TenantContext";
import { useTranslation } from "../hooks/useTranslation";

// Consent screen for the PingUp WordPress plugin connect flow (OAuth2-style
// authorization code + PKCE). The WP plugin sends the admin here; on approval
// we mint a one-time code via POST /api/connect/authorize and redirect the
// browser back to the plugin callback with code + state.

const API_BASE = import.meta.env.VITE_API_BASE_URL || "/api";

const ALLOWED_SCOPES = ["bots:read"];
const CODE_CHALLENGE_RE = /^[A-Za-z0-9_-]{43,128}$/;

// TODO: migrate to i18n locales (src/i18n/locales/*.json carry uncommitted
// changes from another feature, so the strings live inline for this draft).
const STRINGS = {
  it: {
    checkingAuth: "Verifica dell'autenticazione...",
    invalidTitle: "Richiesta non valida",
    invalidDescription:
      "La richiesta di connessione contiene parametri mancanti o non validi. Torna al tuo sito WordPress e riavvia la connessione.",
    consentTitleSuffix: "chiede di accedere al tuo account PingUp",
    consentDescription: "Autorizzando, il sito potrà:",
    scopeLabels: {
      "bots:read": "Vedere l'elenco dei tuoi bot",
    },
    tenantLabel: "Organizzazione",
    tenantPlaceholder: "Seleziona un'organizzazione",
    noTenants: "Nessuna organizzazione disponibile per questo account.",
    authorize: "Autorizza",
    authorizing: "Autorizzazione in corso...",
    deny: "Nega",
    authorizeFailed: "Autorizzazione non riuscita. Riprova.",
    redirectNotice: (host) => `Dopo la conferma verrai reindirizzato a ${host}.`,
    errors: {
      site_url:
        "site_url mancante o non valido (è richiesta un'origine https senza percorso).",
      redirect_uri: "redirect_uri mancante o non valido.",
      redirect_uri_mismatch:
        "redirect_uri deve avere la stessa origine di site_url.",
      code_challenge: "code_challenge mancante o non valido.",
      code_challenge_method: 'code_challenge_method deve essere "S256".',
      scopes: "scopes contiene valori non supportati.",
      state: "state mancante.",
    },
  },
  en: {
    checkingAuth: "Checking authentication...",
    invalidTitle: "Invalid request",
    invalidDescription:
      "The connection request has missing or invalid parameters. Go back to your WordPress site and start the connection again.",
    consentTitleSuffix: "is asking to access your PingUp account",
    consentDescription: "By authorizing, the site will be able to:",
    scopeLabels: {
      "bots:read": "See the list of your bots",
    },
    tenantLabel: "Organization",
    tenantPlaceholder: "Select an organization",
    noTenants: "No organization available for this account.",
    authorize: "Authorize",
    authorizing: "Authorizing...",
    deny: "Deny",
    authorizeFailed: "Authorization failed. Please try again.",
    redirectNotice: (host) => `After confirming you will be redirected to ${host}.`,
    errors: {
      site_url:
        "site_url is missing or invalid (an https origin without a path is required).",
      redirect_uri: "redirect_uri is missing or invalid.",
      redirect_uri_mismatch:
        "redirect_uri must have the same origin as site_url.",
      code_challenge: "code_challenge is missing or invalid.",
      code_challenge_method: 'code_challenge_method must be "S256".',
      scopes: "scopes contains unsupported values.",
      state: "state is missing.",
    },
  },
};

// Parse an http(s) URL; https is enforced except for localhost/127.0.0.1
// (same rules as the API-side validation in routes/connect.js).
const parseHttpUrl = (raw) => {
  if (!raw) return null;
  let url;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  const isLocal = url.hostname === "localhost" || url.hostname === "127.0.0.1";
  if (url.protocol === "http:" && !isLocal) return null;
  return url;
};

const validateParams = (searchParams) => {
  const errors = [];

  const siteUrl = parseHttpUrl(searchParams.get("site_url"));
  // site_url must be a bare origin: no path, query string or fragment
  const siteUrlOk =
    siteUrl && siteUrl.pathname === "/" && !siteUrl.search && !siteUrl.hash;
  if (!siteUrlOk) errors.push("site_url");

  const rawRedirectUri = searchParams.get("redirect_uri");
  const redirectUrl = parseHttpUrl(rawRedirectUri);
  if (!redirectUrl) {
    errors.push("redirect_uri");
  } else if (siteUrlOk && redirectUrl.origin !== siteUrl.origin) {
    errors.push("redirect_uri_mismatch");
  }

  const codeChallenge = searchParams.get("code_challenge") || "";
  if (!CODE_CHALLENGE_RE.test(codeChallenge)) errors.push("code_challenge");

  if (searchParams.get("code_challenge_method") !== "S256") {
    errors.push("code_challenge_method");
  }

  const rawScopes = searchParams.get("scopes");
  let scopes = [...ALLOWED_SCOPES];
  if (rawScopes) {
    scopes = rawScopes
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (
      scopes.length === 0 ||
      !scopes.every((scope) => ALLOWED_SCOPES.includes(scope))
    ) {
      errors.push("scopes");
    }
  }

  const state = searchParams.get("state");
  if (!state) errors.push("state");

  if (errors.length > 0) return { ok: false, errors };

  return {
    ok: true,
    params: {
      siteUrl: siteUrl.origin,
      siteHost: siteUrl.host,
      siteName: searchParams.get("site_name") || "",
      // Keep the raw string: the API requires an exact redirect_uri match at
      // token exchange, so we must not normalize what the plugin sent.
      redirectUri: rawRedirectUri,
      state,
      codeChallenge,
      scopes,
    },
  };
};

const ConnectAuthorize = () => {
  const { isAuthenticated, isLoading, tenants } = useAuth();
  const { activeTenant } = useTenant();
  const { currentLanguage } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const lang = currentLanguage?.toLowerCase().startsWith("it") ? "it" : "en";
  const t = STRINGS[lang];

  const validation = useMemo(() => validateParams(searchParams), [searchParams]);

  // Default the tenant selection to the app's active tenant once auth resolves
  useEffect(() => {
    if (selectedTenantId) return;
    const fallback =
      activeTenant?.tenant_id ||
      activeTenant?._id ||
      tenants?.[0]?.tenant_id ||
      tenants?.[0]?._id;
    if (fallback) setSelectedTenantId(fallback);
  }, [selectedTenantId, activeTenant, tenants]);

  const buildRedirectUrl = (extraParams) => {
    // redirect_uri may already carry a query string (admin-post.php?action=...),
    // so append via URLSearchParams instead of string concatenation.
    const url = new URL(validation.params.redirectUri);
    Object.entries(extraParams).forEach(([key, value]) =>
      url.searchParams.set(key, value)
    );
    return url.toString();
  };

  const handleAuthorize = async () => {
    if (!selectedTenantId || busy) return;
    setBusy(true);
    setSubmitError("");

    try {
      const { params } = validation;
      const body = {
        site_url: params.siteUrl,
        redirect_uri: params.redirectUri,
        code_challenge: params.codeChallenge,
        code_challenge_method: "S256",
        scopes: params.scopes,
      };
      if (params.siteName) body.site_name = params.siteName;

      // Bare axios call instead of the shared api client: its request
      // interceptor overwrites X-Tenant-Id with the persisted active tenant,
      // while here the tenant chosen in the selector must win.
      const response = await axios.post(`${API_BASE}/connect/authorize`, body, {
        withCredentials: true,
        headers: { "X-Tenant-Id": selectedTenantId },
      });

      window.location.assign(
        buildRedirectUrl({ code: response.data.code, state: params.state })
      );
    } catch (err) {
      console.error("Connect authorize failed:", err);
      const serverMessage =
        err.response?.data?.message || err.response?.data?.error;
      setSubmitError(serverMessage || t.authorizeFailed);
      setBusy(false);
    }
  };

  const handleDeny = () => {
    if (busy) return;
    setBusy(true);
    window.location.assign(
      buildRedirectUrl({ error: "access_denied", state: validation.params.state })
    );
  };

  // 1. Invalid params -> error card. Never redirect to redirect_uri here
  //    (an unvalidated redirect would turn this page into an open redirector).
  if (!validation.ok) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                {t.invalidTitle}
              </CardTitle>
              <CardDescription>{t.invalidDescription}</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
                {validation.errors.map((key) => (
                  <li key={key}>{t.errors[key]}</li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingPage message={t.checkingAuth} />;
  }

  // 2. Not logged in -> existing login flow with a return URL that preserves
  //    the whole OAuth query string, so the consent request survives the trip.
  if (!isAuthenticated) {
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />;
  }

  const { params } = validation;
  const hasTenants = tenants && tenants.length > 0;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl leading-snug">
              <span className="font-bold break-all">{params.siteHost}</span>{" "}
              <span className="font-normal">{t.consentTitleSuffix}</span>
            </CardTitle>
            <CardDescription className="break-all">
              {params.siteName
                ? `${params.siteName} · ${params.siteUrl}`
                : params.siteUrl}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <p className="text-sm font-medium mb-2">{t.consentDescription}</p>
              <ul className="space-y-2">
                {params.scopes.map((scope) => (
                  <li
                    key={scope}
                    className="flex items-center gap-2 text-sm text-muted-foreground"
                  >
                    <Check className="h-4 w-4 shrink-0 text-green-600" />
                    {t.scopeLabels[scope] || scope}
                  </li>
                ))}
              </ul>
            </div>

            {hasTenants && tenants.length > 1 && (
              <div className="space-y-2">
                <Label htmlFor="connect-tenant">{t.tenantLabel}</Label>
                <Select
                  value={selectedTenantId}
                  onValueChange={setSelectedTenantId}
                  disabled={busy}
                >
                  <SelectTrigger id="connect-tenant">
                    <SelectValue placeholder={t.tenantPlaceholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {tenants.map((tenant) => {
                      const tenantId = tenant.tenant_id || tenant._id;
                      return (
                        <SelectItem key={tenantId} value={tenantId}>
                          {tenant.name}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            )}

            {!hasTenants && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-md text-sm">
                {t.noTenants}
              </div>
            )}

            {submitError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                {submitError}
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              {t.redirectNotice(params.siteHost)}
            </p>
          </CardContent>
          <CardFooter className="gap-3">
            <Button
              variant="outline"
              className="w-full"
              onClick={handleDeny}
              disabled={busy}
            >
              {t.deny}
            </Button>
            <Button
              className="w-full"
              onClick={handleAuthorize}
              disabled={busy || !hasTenants || !selectedTenantId}
            >
              {busy ? t.authorizing : t.authorize}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default ConnectAuthorize;
